import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import { Button } from '../../../../components/ui/button';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { BlogResponse, blogService } from '../../services/blogApi';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../../components/ui/tabs';
import BlogTable from './BlogTable';

const SharedBlogsTab = () => {
  const [sharedBlogs, setSharedBlogs] = useState<BlogResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSharedBlogs = async () => {
    try {
      setIsLoading(true);
      const response = await blogService.getSharedBlogs();
      if (response.success && response.data) {
        setSharedBlogs(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch shared blogs:', error);
      toast.error('Failed to load shared blogs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSharedBlogs();
  }, []);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Shared With Me</h3>
        <Button
          variant="customSecondary"
          size="sm"
          onClick={fetchSharedBlogs}
          className="gap-2 bg-secondary"
          disabled={isLoading}
        >
          <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((n) => (
            <div key={n} className="h-10 bg-gray-100 animate-pulse rounded" />
          ))}
        </div>
      ) : sharedBlogs.length === 0 ? (
        <div className="text-center py-8 bg-background-card text-text-primary rounded-lg">
          <ExternalLink className="w-12 h-12  mx-auto mb-3" />
          <h3 className="text-lg font-medium  mb-1">No shared blogs</h3>
          <p className="text-sm ">No blogs have been shared with you yet</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>File Name</TableHead>
              <TableHead>Shared By</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sharedBlogs.map((blog) => (
              <TableRow key={blog.id}>
                <TableCell className="font-medium">{blog.title}</TableCell>
                <TableCell>{blog.file_name}</TableCell>
                <TableCell>{blog.blog.context}</TableCell>
                <TableCell>
                  <Link to={`/ai-blog-writer/blog?id=${blog.id}`}>
                    <Button variant="ghost" size="icon" title="Open">
                      <ExternalLink size={16} />
                    </Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

interface BlogTableWithTabsProps {
  blogs: BlogResponse[];
  onRefresh: () => void;
  isLoading: boolean;
}

const BlogTableWithTabs: React.FC<BlogTableWithTabsProps> = ({
  blogs,
  onRefresh,
  isLoading,
}) => {
  return (
    <Tabs defaultValue="my-blogs" className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="my-blogs">My Blogs</TabsTrigger>
        <TabsTrigger value="shared">Shared With Me</TabsTrigger>
      </TabsList>
      <TabsContent value="my-blogs">
        <BlogTable blogs={blogs} onRefresh={onRefresh} isLoading={isLoading} />
      </TabsContent>
      <TabsContent value="shared">
        <SharedBlogsTab />
      </TabsContent>
    </Tabs>
  );
};

export default BlogTableWithTabs;
